import { Menu, MenuItem } from '@material-ui/core';
import LayoutContext from 'components/Layout/LayoutContext';
import { AnexoEnum, AnexoPautaType } from 'helpers/enums/anexoPauta';
import {
  downloadFileFromArrayBuffer,
  downloadFileFromBlobOrFile,
  getFileNameFromContentDispositionOrDefault,
  isNullOrUndefined,
} from 'helpers/utils/HTMLUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import translation from 'locales/translation';
import {
  Button,
  ButtonThemes,
  Checkbox,
  CloudDownloadIcon,
  Col,
  ControlLabel,
  EditIcon,
  IconButton,
  Input,
  PercentLoaderIcon,
  Row,
  ToastTypes,
  TrashIcon,
} from 'plataforma-fundacao-componentes';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  getDownloadAnexo,
  getDownloadAnexoRegister,
} from 'services/anexoPauta';

import { CircleCheck, CircleTimes } from 'components/Icons';
import { TextRange } from 'helpers/utils/StringUtils';
import moment from 'moment';
import styles from './styles.module.scss';

interface Props {
  idEvento: number;
  idPauta: number;
  anexo: AnexoPautaType;
  onRemove: (id: number | undefined) => void;
  onEdit: (anexo: AnexoPautaType) => void;
  register?: boolean;
  isEditing?: (e: boolean) => any;
}

const DESC_LIMIT = { [AnexoEnum.DOCUMENTO]: 300, [AnexoEnum.VIDEO]: 200 };

export default function AnexoPautaItem(props: Props) {
  const [editable, setEditable] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    setDescricao(props.anexo.descricao);
  }, [props.anexo.descricao]);

  const [visivelAssociado, setVisivelAssociado] = useState<boolean>(
    props.anexo.flgDisponivelAssociado ?? true
  );

  const { showToast } = useContext(LayoutContext);

  const downloadFile = useCallback(() => {
    setDownloading(true);
    if (props.anexo.file) {
      downloadFileFromBlobOrFile(props.anexo.file);
      setDownloading(false);
    } else if (props.idPauta && props.anexo.oidAnexoPauta) {
      if (props.register) {
        getDownloadAnexoRegister(
          props.idEvento,
          props.idPauta,
          props.anexo.oidAnexoPauta!
        )
          .then((resp) => {
            downloadFileFromArrayBuffer(
              resp.data,
              getFileNameFromContentDispositionOrDefault(
                resp.headers,
                props.anexo.nomDocumento ?? 'Anexo Pauta.pdf'
              )
            );
          })
          .finally(() => {
            setDownloading(false);
          });
      } else {
        getDownloadAnexo(
          props.idEvento,
          props.idPauta,
          props.anexo.oidAnexoPauta!
        )
          .then((resp) => {
            downloadFileFromArrayBuffer(
              resp.data,
              getFileNameFromContentDispositionOrDefault(
                resp.headers,
                props.anexo.nomDocumento ?? 'Anexo Pauta.pdf'
              )
            );
          })
          .finally(() => {
            setDownloading(false);
          });
      }
    } else {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 4000,
        label: languageValues.toasts.error.downloadAnexo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.anexo.file,
    props.anexo.nomDocumento,
    props.anexo.oidAnexoPauta,
    props.idEvento,
    props.idPauta,
  ]);

  const [confirmDelOpened, setConfirmDelOpened] = useState(false);
  const iconRef: any = useRef<HTMLDivElement>();

  return (
    <Row className={styles.root}>
      <Menu
        open={confirmDelOpened}
        onClose={() => setConfirmDelOpened(false)}
        anchorEl={iconRef.current}
      >
        <MenuItem disabled>Deseja realmente remover este anexo?</MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmDelOpened(false);
          }}
        >
          {languageValues.buttons.no}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmDelOpened(false);
            props.onRemove(props.anexo.oidAnexoPauta ?? undefined);
          }}
        >
          {languageValues.buttons.yes}
        </MenuItem>
      </Menu>
      <Row style={{ padding: '16px' }}>
        <Col cols={[11, 11, 11, 11, 11]}>
          <Row>
            <Col noPadding>
              <strong style={{ lineBreak: 'anywhere' }}>
                {props.anexo.tipoAnexo === AnexoEnum.DOCUMENTO
                  ? props.anexo.file?.name ?? props.anexo?.nomDocumento ?? ''
                  : props.anexo.url ?? ''}
              </strong>
            </Col>
            {!isNullOrUndefined(props.anexo.dataCadastro) ? (
              <Col noPadding style={{ paddingTop: '6px', opacity: '0.6' }}>
                <span>
                  {moment(props.anexo.dataCadastro).format(
                    'DD/MM/YYYY - HH:mm:ss'
                  )}
                </span>
              </Col>
            ) : (
              <Fragment />
            )}
          </Row>
        </Col>

        <Col cols={[1, 1, 1, 1, 1]} buttonActionsCol end>
          <IconButton
            icon={<TrashIcon />}
            onClick={(e) => {
              if (e.target) iconRef.current = e.target;
              setConfirmDelOpened(true);
            }}
            disabled={false}
          />
        </Col>
        {props.anexo.tipoAnexo === AnexoEnum.DOCUMENTO && (
          <Col>
            <Button
              theme={ButtonThemes.Secondary}
              disabled={downloading}
              rightIcon={
                downloading ? (
                  <PercentLoaderIcon indefinido />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              onClick={downloadFile}
              data-testid={'document-download'}
            >
              Baixar
            </Button>
          </Col>
        )}
        <Col cols={[11, 11, 11, 11, 11]}>
          <Input
            value={descricao}
            placeholder={'Sem descrição'}
            onChange={(e) => {
              setDescricao(
                TextRange(e.target.value, DESC_LIMIT[props.anexo.tipoAnexo])
              );
            }}
            disabled={!editable}
            maxLength={300}
            counter={editable}
            data-testid={'document-description'}
          />
        </Col>
        <Col cols={[1, 1, 1, 1, 1]} buttonActionsCol end>
          <IconButton
            onClick={() => {
              setEditable(true);
            }}
            disabled={editable}
            data-testid={'document-edit-button'}
            icon={<EditIcon />}
          />
        </Col>
        <Col noPaddingTop style={{ marginTop: '-8px' }}>
          <ControlLabel
            label={translation.AnexoPautaItem.visivelLabel}
            control={
              <Checkbox
                value={visivelAssociado}
                onChange={(value) => {
                  setVisivelAssociado(value);
                }}
              />
            }
          />
        </Col>
        <Col buttonActionsCol end>
          {editable ? (
            <Button
              theme={ButtonThemes.DangerDarkerOutline}
              leftIcon={<CircleTimes />}
              disabled={!editable}
              onClick={() => {
                setDescricao(props.anexo.descricao);
                setVisivelAssociado(
                  props.anexo.flgDisponivelAssociado ?? false
                );
                setEditable(false);
              }}
              data-testid={'document-cancel-button'}
            >
              {translation.EditarDocumentosModal.buttonCancel}
            </Button>
          ) : (
            <Fragment />
          )}
          <Button
            theme={ButtonThemes.Primary}
            disabled={
              editable &&
              props.anexo.flgDisponivelAssociado === visivelAssociado
                ? !descricao ||
                  (descricao === props.anexo.descricao &&
                    visivelAssociado === props.anexo.flgDisponivelAssociado)
                : props.anexo.flgDisponivelAssociado === visivelAssociado
            }
            onClick={() => {
              props.onEdit({
                ...props.anexo,
                descricao,
                flgDisponivelAssociado: visivelAssociado,
              });
              setEditable(false);
            }}
            leftIcon={<CircleCheck noFill />}
            data-testid={'document-change-button'}
          >
            Editar
          </Button>
        </Col>
      </Row>
    </Row>
  );
}
